import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { notesApi, categoriesApi } from '../api';
import NoteCard from '../components/notes/NoteCard';
import NoteModal from '../components/notes/NoteModal';
import NoteDetailModal from '../components/notes/NoteDetailModal';
import ConfirmDialog from '../components/common/ConfirmDialog';
import TagInput from '../components/common/TagInput';
import { Icon } from '../components/common/Icons';
import { toast } from 'react-toastify';

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const rawType = searchParams.get('type') || 'notes';
  const typeParam = (rawType === 'folders' || rawType === 'categories') ? 'categories' : 'notes';
  const titleParam = searchParams.get('title') || searchParams.get('q') || '';
  const categoryIdParam = searchParams.get('categoryId') || searchParams.get('folderId') || '';
  const tagsParam = useMemo(() => {
    const raw = searchParams.get('tags');
    return raw ? raw.split(',').filter(Boolean) : [];
  }, [searchParams]);
  const nameParam = searchParams.get('name') || '';

  const [searchType, setSearchType] = useState(typeParam);

  // Notes Search state
  const [title, setTitle] = useState(titleParam);
  const [selectedCategoryId, setSelectedCategoryId] = useState(categoryIdParam);
  const [tags, setTags] = useState(tagsParam);

  // Category Search state
  const [categoryName, setCategoryName] = useState(nameParam);

  // Results & Loading
  const [categoriesList, setCategoriesList] = useState([]);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [validationError, setValidationError] = useState('');

  // Modals
  const [editNote, setEditNote] = useState(null);
  const [viewNote, setViewNote] = useState(null);
  const [deleteNote, setDeleteNote] = useState(null);

  // Custom Category Picker state
  const [categoryPickerOpen, setCategoryPickerOpen] = useState(false);
  const [categorySearchQuery, setCategorySearchQuery] = useState('');
  const categoryPickerRef = useRef(null);

  // Fetch available categories for dropdown
  useEffect(() => {
    categoriesApi.list()
      .then(r => setCategoriesList(r.data))
      .catch(() => {});
  }, []);

  // Click outside to close category picker
  useEffect(() => {
    if (!categoryPickerOpen) return;
    const handleClickOutside = (e) => {
      if (categoryPickerRef.current && !categoryPickerRef.current.contains(e.target)) {
        setCategoryPickerOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [categoryPickerOpen]);

  const selectedCategoryDisplayName = useMemo(() => {
    if (!selectedCategoryId) return 'All Categories';
    const found = categoriesList.find(c => String(c.id) === String(selectedCategoryId));
    return found ? found.name : 'All Categories';
  }, [selectedCategoryId, categoriesList]);

  const filteredCategoriesForPicker = useMemo(() => {
    if (!categorySearchQuery.trim()) return categoriesList;
    const q = categorySearchQuery.toLowerCase();
    return categoriesList.filter(c => c.name.toLowerCase().includes(q));
  }, [categoriesList, categorySearchQuery]);

  const doNotesSearch = useCallback(async (t, catId, tg) => {
    const cleanTitle = (t || '').trim();
    const hasTags = tg && tg.length > 0;
    const hasCategory = !!catId;

    if (!cleanTitle && !hasTags && !hasCategory) {
      setValidationError('Please provide at least a title, category, or hashtag to search.');
      return;
    }

    setValidationError('');
    setLoading(true);
    setSearched(true);
    try {
      const payload = {
        title: cleanTitle || undefined,
        categoryId: catId ? Number(catId) : undefined,
        tags: hasTags ? tg : undefined,
      };
      const { data } = await notesApi.searchNotes(payload);
      setResults(data || []);
    } catch (err) {
      setResults([]);
      toast.error(err.response?.data?.message || 'Search failed');
    } finally {
      setLoading(false);
    }
  }, []);

  const doCategorySearch = useCallback(async (name) => {
    const cleanName = (name || '').trim();
    if (!cleanName) {
      setValidationError('Please enter a category name to search.');
      return;
    }

    setValidationError('');
    setLoading(true);
    setSearched(true);
    try {
      const { data } = await categoriesApi.searchCategories({ name: cleanName });
      setResults(data || []);
    } catch (err) {
      setResults([]);
      toast.error(err.response?.data?.message || 'Category search failed');
    } finally {
      setLoading(false);
    }
  }, []);

  // Run search when URL query parameters change
  useEffect(() => {
    if (typeParam === 'categories') {
      setSearchType('categories');
      if (nameParam) {
        setCategoryName(nameParam);
        doCategorySearch(nameParam);
      }
    } else {
      setSearchType('notes');
      setTitle(titleParam);
      setSelectedCategoryId(categoryIdParam);
      setTags(tagsParam);
      if (titleParam || categoryIdParam || tagsParam.length > 0) {
        doNotesSearch(titleParam, categoryIdParam, tagsParam);
      }
    }
  }, [typeParam, titleParam, categoryIdParam, tagsParam, nameParam, doNotesSearch, doCategorySearch]);

  const handleNotesSubmit = (e) => {
    e?.preventDefault();
    if (title.trim()) {
      setSearchParams({ q: title.trim() });
    }
    doNotesSearch(title, selectedCategoryId, tags);
  };

  const handleCategorySubmit = (e) => {
    e?.preventDefault();
    doCategorySearch(categoryName);
  };

  const switchMode = (mode) => {
    setSearchType(mode);
    setResults([]);
    setSearched(false);
    setValidationError('');
  };

  const handleDelete = async () => {
    if (!deleteNote) return;
    try {
      await notesApi.delete(deleteNote.id);
      toast.success(`"${deleteNote.title}" moved to Trash`);
      setResults(r => r.filter(d => d.id !== deleteNote.id));
      setDeleteNote(null);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete note');
    }
  };

  const handleToggleStar = async (note) => {
    try {
      const { data: updated } = await notesApi.toggleStar(note.id);
      setResults(prev => prev.map(d => d.id === note.id ? updated : d));
    } catch {
      toast.error('Failed to update star');
    }
  };

  return (
    <div className="search-page-container">
      {/* Header */}
      <div className="page-header">
        <div className="page-header-content">
          <h1 className="page-title">Search Workspace</h1>
        </div>
      </div>

      {/* Main Search Panel Card */}
      <div className="search-panel-card">
        {/* Search Type Slider */}
        <div className="search-slider-toggle" style={{ maxWidth: '340px', marginBottom: '1.25rem' }}>
          <div className={`search-slider-pill ${searchType === 'categories' ? 'categories' : 'notes'}`} />
          <button
            type="button"
            className={`search-slider-btn ${searchType === 'notes' ? 'active' : ''}`}
            onClick={() => switchMode('notes')}
          >
            <Icon name="note" size={15} />
            <span>Notes Search</span>
          </button>
          <button
            type="button"
            className={`search-slider-btn ${searchType === 'categories' ? 'active' : ''}`}
            onClick={() => switchMode('categories')}
          >
            <Icon name="category" size={15} />
            <span>Category Search</span>
          </button>
        </div>

        {/* Validation Alert */}
        {validationError && (
          <div className="alert-custom error search-validation-banner">
            <Icon name="alert-triangle" size={15} />
            <span>{validationError}</span>
          </div>
        )}

        {/* Notes Search Form */}
        {searchType === 'notes' ? (
          <form onSubmit={handleNotesSubmit} className="search-form-layout">
            <div className="search-form-grid">
              <div className="form-group">
                <label className="form-label" htmlFor="page-search-title">
                  Title
                </label>
                <div className="input-with-icon-wrap">
                  <Icon name="note" size={16} className="input-leading-icon" />
                  <input
                    id="page-search-title"
                    className="form-input with-leading-icon"
                    placeholder="e.g. Project Plan, Meeting Notes..."
                    value={title}
                    onChange={e => {
                      setTitle(e.target.value);
                      if (validationError) setValidationError('');
                    }}
                    autoFocus
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">
                  Category
                </label>
                <div className="custom-folder-picker-wrapper" ref={categoryPickerRef}>
                  <button
                    type="button"
                    className={`custom-folder-picker-trigger ${categoryPickerOpen ? 'open' : ''}`}
                    onClick={() => setCategoryPickerOpen(prev => !prev)}
                  >
                    <div className="folder-picker-current">
                      <Icon name="category" size={16} className="folder-picker-icon" />
                      <span>{selectedCategoryDisplayName}</span>
                      {selectedCategoryDisplayName === 'General' && (
                        <span className="category-default-chip-grey">Default</span>
                      )}
                    </div>
                    <Icon name={categoryPickerOpen ? 'chevron-up' : 'chevron-down'} size={15} className="folder-picker-arrow" />
                  </button>

                  {categoryPickerOpen && (
                    <div className="custom-folder-picker-menu">
                      <div className="folder-picker-search-bar">
                        <Icon name="search" size={14} className="folder-picker-search-icon" />
                        <input
                          className="folder-picker-search-input"
                          placeholder="Search categories..."
                          value={categorySearchQuery}
                          onChange={e => setCategorySearchQuery(e.target.value)}
                          autoFocus
                        />
                        {categorySearchQuery && (
                          <button
                            type="button"
                            className="folder-picker-clear-search"
                            onClick={() => setCategorySearchQuery('')}
                          >
                            <Icon name="close" size={12} />
                          </button>
                        )}
                      </div>

                      <div className="folder-picker-options-list">
                        {/* All Categories Option */}
                        <div
                          className={`folder-picker-option ${!selectedCategoryId ? 'selected' : ''}`}
                          onClick={() => {
                            setSelectedCategoryId('');
                            setCategoryPickerOpen(false);
                          }}
                        >
                          <div className="folder-picker-option-left">
                            <Icon name="category" size={15} />
                            <span className="folder-picker-option-name">All Categories</span>
                          </div>
                          {!selectedCategoryId && <Icon name="check" size={15} className="folder-picker-check" />}
                        </div>

                        {/* Category options without note count */}
                        {filteredCategoriesForPicker.map(c => {
                          const isSelected = String(selectedCategoryId) === String(c.id);
                          const isDefault = c.name === 'General' || c.name === 'My Folder';
                          return (
                            <div
                              key={c.id}
                              className={`folder-picker-option ${isSelected ? 'selected' : ''}`}
                              onClick={() => {
                                setSelectedCategoryId(c.id);
                                setCategoryPickerOpen(false);
                              }}
                            >
                              <div className="folder-picker-option-left">
                                <Icon name="category" size={15} />
                                <span className="folder-picker-option-name">{c.name}</span>
                                {isDefault && <span className="category-default-chip-grey">Default</span>}
                              </div>
                              {isSelected && <Icon name="check" size={15} className="folder-picker-check" />}
                            </div>
                          );
                        })}

                        {filteredCategoriesForPicker.length === 0 && categorySearchQuery && (
                          <div className="folder-picker-no-results">
                            No categories found matching "{categorySearchQuery}"
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="form-group full-width">
                <label className="form-label">
                  Filter by Hashtags <span className="label-hint">(Optional)</span>
                </label>
                <TagInput
                  tags={tags}
                  onChange={newTags => {
                    setTags(newTags);
                    if (validationError) setValidationError('');
                  }}
                  placeholder="Add hashtags to filter (press Enter or Tab)..."
                />
              </div>
            </div>

            <div className="search-panel-footer">
              <div />
              <div className="search-panel-btns">
                {(title || tags.length > 0 || selectedCategoryId) && (
                  <button
                    type="button"
                    className="btn-ghost"
                    onClick={() => {
                      setTitle('');
                      setTags([]);
                      setSelectedCategoryId('');
                      setResults([]);
                      setSearched(false);
                      setSearchParams({});
                    }}
                  >
                    Reset
                  </button>
                )}
                <button
                  type="submit"
                  className="btn-primary-custom"
                  disabled={loading}
                >
                  {loading ? <div className="spinner sm" /> : <Icon name="search" size={16} />}
                  <span>Search</span>
                </button>
              </div>
            </div>
          </form>
        ) : (
          /* Category Search Form */
          <form onSubmit={handleCategorySubmit} className="search-form-layout">
            <div className="form-group">
              <label className="form-label" htmlFor="page-search-categoryname">
                Name
              </label>
              <div className="input-with-icon-wrap">
                <Icon name="category" size={16} className="input-leading-icon" />
                <input
                  id="page-search-categoryname"
                  className="form-input with-leading-icon"
                  placeholder="Enter category name..."
                  value={categoryName}
                  onChange={e => {
                    setCategoryName(e.target.value);
                    if (validationError) setValidationError('');
                  }}
                  autoFocus
                />
              </div>
            </div>

            <div className="search-panel-footer justify-end">
              <div className="search-panel-btns">
                {categoryName && (
                  <button
                    type="button"
                    className="btn-ghost"
                    onClick={() => {
                      setCategoryName('');
                      setResults([]);
                      setSearched(false);
                    }}
                  >
                    Reset
                  </button>
                )}
                <button
                  type="submit"
                  className="btn-primary-custom"
                  disabled={loading || !categoryName.trim()}
                >
                  {loading ? <div className="spinner sm" /> : <Icon name="search" size={16} />}
                  <span>Search</span>
                </button>
              </div>
            </div>
          </form>
        )}
      </div>

      {/* Results Header */}
      {searched && (
        <div className="search-results-summary-bar">
          <span className="results-count-text">
            Found <span className="font-semibold text-primary">{results.length}</span> matching{' '}
            {searchType === 'notes'
              ? (results.length === 1 ? 'note' : 'notes')
              : (results.length === 1 ? 'category' : 'categories')}
          </span>
        </div>
      )}

      {/* Results Display */}
      {loading ? (
        <div className="loading-state-wrap">
          <div className="spinner" />
          <p className="loading-text">Searching indexed workspace...</p>
        </div>
      ) : searched && results.length === 0 ? (
        <div className="empty-state-card">
          <div className="empty-state-icon-wrap">
            <Icon name="search" size={36} />
          </div>
          <h2 className="empty-state-title">No matches found</h2>
          <p className="empty-state-desc">
            Try adjusting your keyword spelling, hashtags, or category filter.
          </p>
        </div>
      ) : searched && searchType === 'notes' ? (
        <div className="notes-responsive-grid docs-responsive-grid">
          {results.map(note => (
            <NoteCard
              key={note.id}
              note={note}
              layout="grid"
              onClick={() => setViewNote(note)}
              onEdit={d => setEditNote(d)}
              onDelete={d => setDeleteNote(d)}
              onToggleStar={handleToggleStar}
            />
          ))}
        </div>
      ) : searched && searchType === 'categories' ? (
        <div className="categories-grid-rectangles">
          {results.map(category => {
            const isDefaultCat = category.name === 'My Folder' || category.name === 'General';
            return (
              <div
                key={category.id}
                className="category-rect-card"
                onClick={() => navigate(`/categories/${category.id}`)}
                role="button"
                tabIndex={0}
              >
                <div className="category-rect-top">
                  <div className="category-rect-top-left">
                    <span className="category-rect-mini-icon">
                      <Icon name="category" size={13} />
                    </span>
                    {isDefaultCat && (
                      <span className="category-default-badge">Default</span>
                    )}
                  </div>
                  <div className="category-rect-actions" onClick={e => e.stopPropagation()}>
                    <button
                      type="button"
                      className={`action-btn-pill btn-star ${(category.starred ?? category.favorite) ? 'active' : ''}`}
                      title={(category.starred ?? category.favorite) ? 'Unstar Category' : 'Star Category'}
                      onClick={async (e) => {
                        e.stopPropagation();
                        try {
                          const { data: updated } = await categoriesApi.toggleStar(category.id);
                          setResults(prev => prev.map(c => c.id === category.id ? { ...c, ...updated, starred: !!(updated.starred ?? updated.favorite), favorite: !!(updated.starred ?? updated.favorite) } : c));
                          const isStarred = !!(updated.starred ?? updated.favorite);
                          toast.success(isStarred ? `Starred "${category.name}"` : `Unstarred "${category.name}"`);
                          window.dispatchEvent(new CustomEvent('memora:category-saved', { detail: updated }));
                        } catch {
                          toast.error('Failed to update category star');
                        }
                      }}
                      aria-label={(category.starred ?? category.favorite) ? 'Unstar category' : 'Star category'}
                    >
                      <Icon name="star" size={14} filled={!!(category.starred ?? category.favorite)} />
                    </button>
                  </div>
                </div>

                <div className="category-rect-body">
                  <h3 className="category-rect-name" title={category.name}>
                    {category.name}
                  </h3>
                  <span className="category-rect-pill">
                    {category.noteCount} {category.noteCount === 1 ? 'note' : 'notes'}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      ) : !searched ? (
        <div className="empty-state-card sm">
          <div className="empty-state-icon-wrap" style={{ width: 48, height: 48 }}>
            <Icon name="search" size={24} />
          </div>
          <h3 className="empty-state-title" style={{ fontSize: '1.05rem' }}>
            Ready to search
          </h3>
          <p className="empty-state-desc" style={{ fontSize: '0.85rem' }}>
            Enter a title, select a category, or add hashtags to quickly find your notes and categories.
          </p>
        </div>
      ) : null}

      {/* Modals */}
      {editNote && (
        <NoteModal
          note={editNote}
          onClose={() => setEditNote(null)}
          onSaved={() => doNotesSearch(title, selectedCategoryId, tags)}
        />
      )}

      {viewNote && (
        <NoteDetailModal
          note={viewNote}
          onClose={() => setViewNote(null)}
          onEdit={d => {
            setViewNote(null);
            setEditNote(d);
          }}
          onDelete={d => {
            setViewNote(null);
            setDeleteNote(d);
          }}
          onNoteUpdated={updated => {
            setResults(prev => prev.map(d => d.id === updated.id ? updated : d));
          }}
        />
      )}

      {deleteNote && (
        <ConfirmDialog
          title="Move Note to Trash?"
          message={`"${deleteNote.title}" will be moved to Trash. You can recover it anytime within 7 days.`}
          confirmLabel="Move to Trash"
          onConfirm={handleDelete}
          onCancel={() => setDeleteNote(null)}
          danger
        />
      )}


    </div>
  );
}
